import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { IProgram } from "../../interfaces/models/IEvent";
import { MemberEventAttendee } from "../../interfaces/models/IMemberEventAttendee";
import { IRegistration } from "../../interfaces/models/IRegistration";
import {
  getMemberEventAttendees,
  getRegistrationByPersonAndEvent,
} from "../../Services/Registration";
import {
  attendeesStateFromCache,
  clearEventDetailCache,
  getAttendeesCacheEntry,
  getEnrichedProgramsFromCache,
  getRegistrationCacheEntry,
  registrationStateFromCache,
  setAttendeesCacheError,
  setAttendeesCachePromise,
  setAttendeesCacheSuccess,
  setEnrichedProgramsCache,
  setRegistrationCacheError,
  setRegistrationCachePromise,
  setRegistrationCacheSuccess,
  type AttendeesLoadState,
  type RegistrationLoadState,
} from "./eventDetailCache";
import { programsNeedEnrichment } from "./programEnrichment";

type EventDetailDataContextValue = {
  eventId: string;
  attendeesState: AttendeesLoadState;
  ensureAttendeesLoaded: () => Promise<MemberEventAttendee[]>;
  hasAttendees: boolean | null;
  registrationState: RegistrationLoadState;
  ensureRegistrationLoaded: (
    personId: string,
  ) => Promise<IRegistration | null>;
  getCachedEnrichedPrograms: (
    programs: IProgram[] | undefined,
  ) => IProgram[] | null;
  setCachedEnrichedPrograms: (
    programs: IProgram[] | undefined,
    enrichedPrograms: IProgram[],
  ) => void;
};

const EventDetailDataContext = createContext<EventDetailDataContextValue | null>(
  null,
);

type EventDetailDataProviderProps = {
  eventId: string;
  children: ReactNode;
  preloadRegistration?: {
    personId: string;
  } | null;
};

function buildProgramsCacheKey(programs: IProgram[] | undefined): string {
  if (!programs?.length) {
    return "empty";
  }

  return JSON.stringify(
    programs.map((program) => ({
      id: program.id,
      speakers: (program.speakers ?? []).map((speaker) => ({
        personId: speaker.personId,
        name: speaker.name,
        photo: speaker.photo,
        title: speaker.title,
        firmLogo: speaker.firmLogo,
      })),
      postEventSpeakers: (program.postEventSpeakers ?? []).map((speaker) => ({
        personId: speaker.personId,
        name: speaker.name,
        photo: speaker.photo,
        title: speaker.title,
        firmLogo: speaker.firmLogo,
      })),
    })),
  );
}

export function EventDetailDataProvider({
  eventId,
  children,
  preloadRegistration,
}: EventDetailDataProviderProps) {
  const [attendeesVersion, setAttendeesVersion] = useState(0);
  const [registrationVersion, setRegistrationVersion] = useState(0);

  useEffect(() => {
    return () => {
      clearEventDetailCache(eventId);
    };
  }, [eventId]);

  const bumpAttendees = useCallback(() => {
    setAttendeesVersion((value) => value + 1);
  }, []);

  const bumpRegistration = useCallback(() => {
    setRegistrationVersion((value) => value + 1);
  }, []);

  const ensureAttendeesLoaded = useCallback(async () => {
    const entry = getAttendeesCacheEntry(eventId);

    if (entry.data) {
      return entry.data;
    }

    if (entry.promise) {
      return entry.promise;
    }

    bumpAttendees();

    const promise = getMemberEventAttendees(eventId)
      .then((data) => {
        setAttendeesCacheSuccess(eventId, data);
        bumpAttendees();
        return data;
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error ? error.message : "Failed to load attendees";
        setAttendeesCacheError(eventId, message);
        bumpAttendees();
        throw error;
      });

    setAttendeesCachePromise(eventId, promise);
    return promise;
  }, [bumpAttendees, eventId]);

  const ensureRegistrationLoaded = useCallback(
    async (personId: string) => {
      const cacheKey = `${eventId}:${personId}`;
      const entry = getRegistrationCacheEntry(cacheKey);

      if (entry.data !== undefined) {
        return entry.data;
      }

      if (entry.promise) {
        return entry.promise;
      }

      bumpRegistration();

      const promise = getRegistrationByPersonAndEvent(personId, eventId)
        .then((data) => {
          setRegistrationCacheSuccess(cacheKey, data);
          bumpRegistration();
          return data;
        })
        .catch((error: unknown) => {
          const message =
            error instanceof Error
              ? error.message
              : "Failed to load registration";
          setRegistrationCacheError(cacheKey, message);
          bumpRegistration();
          throw error;
        });

      setRegistrationCachePromise(cacheKey, promise);
      return promise;
    },
    [bumpRegistration, eventId],
  );

  useEffect(() => {
    if (!preloadRegistration?.personId) {
      return;
    }

    void ensureRegistrationLoaded(preloadRegistration.personId).catch(() => {
      // Registration preload is best-effort and should not block the shell.
    });
  }, [ensureRegistrationLoaded, preloadRegistration?.personId]);

  const attendeesState = useMemo(
    () => attendeesStateFromCache(eventId),
    [attendeesVersion, eventId],
  );

  const registrationCacheKey = preloadRegistration?.personId
    ? `${eventId}:${preloadRegistration.personId}`
    : "";

  const registrationState = useMemo(
    () =>
      registrationCacheKey
        ? registrationStateFromCache(registrationCacheKey)
        : ({ status: "idle" } as RegistrationLoadState),
    [registrationCacheKey, registrationVersion],
  );

  const hasAttendees = useMemo(() => {
    if (attendeesState.status === "success") {
      return attendeesState.data.length > 0;
    }
    if (attendeesState.status === "idle") {
      return null;
    }
    return null;
  }, [attendeesState]);

  const getCachedEnrichedPrograms = useCallback(
    (programs: IProgram[] | undefined) => {
      if (!programs?.length || !programsNeedEnrichment(programs)) {
        return programs ?? [];
      }

      return getEnrichedProgramsFromCache(
        eventId,
        buildProgramsCacheKey(programs),
      );
    },
    [eventId],
  );

  const setCachedEnrichedPrograms = useCallback(
    (programs: IProgram[] | undefined, enrichedPrograms: IProgram[]) => {
      setEnrichedProgramsCache(
        eventId,
        buildProgramsCacheKey(programs),
        enrichedPrograms,
      );
    },
    [eventId],
  );

  const value = useMemo(
    () => ({
      eventId,
      attendeesState,
      ensureAttendeesLoaded,
      hasAttendees,
      registrationState,
      ensureRegistrationLoaded,
      getCachedEnrichedPrograms,
      setCachedEnrichedPrograms,
    }),
    [
      attendeesState,
      ensureAttendeesLoaded,
      ensureRegistrationLoaded,
      eventId,
      getCachedEnrichedPrograms,
      hasAttendees,
      registrationState,
      setCachedEnrichedPrograms,
    ],
  );

  return (
    <EventDetailDataContext.Provider value={value}>
      {children}
    </EventDetailDataContext.Provider>
  );
}

export function useEventDetailData(): EventDetailDataContextValue {
  const context = useContext(EventDetailDataContext);
  if (!context) {
    throw new Error(
      "useEventDetailData must be used within EventDetailDataProvider",
    );
  }
  return context;
}

export function useOptionalEventDetailData():
  | EventDetailDataContextValue
  | null {
  return useContext(EventDetailDataContext);
}
